import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms"
import { MatCheckboxChange } from "@angular/material/checkbox"
import { filter, debounceTime } from "rxjs"
import { ContactType } from "src/app/core/models/common/contact.model"
import { FiscalId, FiscalIdKind } from "src/app/core/models/common/fiscal-id.model"
import { LeadRawValue } from "src/app/core/models/lead/lead-raw-value.model"
import { Lead } from "src/app/core/models/lead/lead.model"
import { Configurations } from "../../configurations/models/configurations.model"
import { RawValueNameConfigurations } from "../../configurations/models/rawvalues-names-configurations.model"
import { ContactValidations, validatePhoneNumber } from "../../utils/validations/contact.validations"
import { ScreenSizeAwareComponent } from "../common-bases/screen-size-aware-component"
import { LabelValueInputResult } from "../inputs/label-value-input/label-value-input.component"
import { HumanizePhonePipe } from "../../utils/pipes/humanize-phone.pipe"

export abstract class LeadFormBase<TForm extends FormGroup> extends ScreenSizeAwareComponent {

    form: TForm | undefined

    protected _lead: Lead | undefined

    constructor(protected config: Configurations, protected readonly typingDebounceTime: number, protected readonly humanizePhonePipe: HumanizePhonePipe,) {
        super()
    }

    protected abstract createForm(): TForm
    protected abstract subscribeToForm(form: TForm): void

    onAddContact(result: LabelValueInputResult, contactsForm: FormArray) {

        const valueFormControl = new FormControl<string>(this.humanizePhonePipe.transform(result.value), [ContactValidations.contactFormValidator(), Validators.required, Validators.maxLength(255)])
        const contact = {
            id: 0, type: validatePhoneNumber(result.value) ? ContactType.Phone : ContactType.Email,
            description: result.label, value: result.value
        }

        valueFormControl.valueChanges.pipe(filter(() => valueFormControl.valid))
            .subscribe(value => contact.value = value!)

        valueFormControl.valueChanges.pipe(
            debounceTime(this.typingDebounceTime),
            filter(() => valueFormControl.valid)
        ).subscribe()

        this._lead!.contacts.unshift(contact)

        this.form!.markAsDirty()

        contactsForm.insert(0,
            new FormGroup({ description: new FormControl<string>({ value: result.label, disabled: true }), value: valueFormControl })
        )
    }

    onAddRawvalue(result: LabelValueInputResult, rawValuesForm: FormArray) {

        const valueFormControl = new FormControl<string>(result.value, [Validators.required, Validators.maxLength(255)])
        const rawvalue = {
            id: 0, description: result.label,
            value: result.value,
            created: new Date(), updated: new Date()
        }

        valueFormControl.valueChanges.pipe(filter(() => valueFormControl.valid))
            .subscribe(value => rawvalue.value = value!)

        valueFormControl.valueChanges.pipe(
            debounceTime(this.typingDebounceTime),
            filter(() => valueFormControl.valid)
        ).subscribe()

        this._lead!.rawValues.unshift(rawvalue)

        this.form!.markAsDirty()

        rawValuesForm.insert(0,
            new FormGroup({ description: new FormControl<string>({ value: result.label, disabled: true }), value: valueFormControl })
        )
    }

    onRemoveContact(contactIndex: number, contactsForm: FormArray) {
        this._lead!.contacts.splice(contactIndex, 1)
        this.form!.markAsDirty()
        contactsForm.removeAt(contactIndex)
        contactsForm.updateValueAndValidity()
    }

    onRemoveRawvalue(rawvalueIndex: number, rawValuesForm: FormArray) {
        this._lead!.rawValues.splice(rawvalueIndex, 1)
        this.form!.markAsDirty()
        rawValuesForm.removeAt(rawvalueIndex)
        rawValuesForm.updateValueAndValidity()
    }

    onBooleanRawvalueChanges(event: MatCheckboxChange, rawvalueForm: FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>, rawValuesForm: FormArray) {
        rawvalueForm.controls.value.setValue(event.checked ? "true" : "false")
        rawValuesForm.updateValueAndValidity()
        this.form!.markAsDirty()
    }


    protected getTaxName(lead: Lead): LeadRawValue | undefined {
        return lead?.rawValues.find(rv => this.config.rawvaluesNamesConfig.taxName.includes(rv.description))
    }

    protected getPiva(lead: Lead): FiscalId | undefined {
        return lead?.fiscalIds.find(f => f.kind == FiscalIdKind.PartitaIva)
    }

    protected getItalianFiscalCode(lead: Lead): FiscalId | undefined {
        return lead?.fiscalIds.find(f => [FiscalIdKind.CfPersonaFisica, FiscalIdKind.CfPersonaGiuridica].includes(f.kind))
    }

    protected getPrivacyAccepted(lead: Lead): LeadRawValue | undefined {
        return lead?.rawValues.find(rv => this.config.rawvaluesNamesConfig.privacyAccepted.includes(rv.description))
    }

    protected getAcknowledgmentAccepted(lead: Lead): LeadRawValue | undefined {
        return lead?.rawValues.find(rv => this.config.rawvaluesNamesConfig.acknowledgmentAccepted.includes(rv.description))
    }

    protected getMarketingAccepted(lead: Lead): LeadRawValue | undefined {
        return lead?.rawValues.find(rv => this.config.rawvaluesNamesConfig.marketingAccepted.includes(rv.description))
    }



    protected setTaxName(name: string) {
        const existingTaxNameRawvalue = this.getTaxName(this._lead!)

        if (existingTaxNameRawvalue) existingTaxNameRawvalue.value = name
        else this._lead!.rawValues.push({
            id: 0, description: this.config.rawvaluesNamesConfig.taxName[0],
            value: name,
            created: new Date(), updated: new Date()
        })
    }

    protected setPiva(piva: string): void {
        const previousPiva = this.getPiva(this._lead!)

        if (!piva) this._lead!.fiscalIds = this._lead!.fiscalIds.filter(f => f.kind != FiscalIdKind.PartitaIva)
        else if (previousPiva) previousPiva.value = piva
        else this._lead!.fiscalIds.push({
            id: 0, description: '', value: piva,
            isPreferred: true, kind: FiscalIdKind.PartitaIva
        })
    }

    protected setItalianFiscalCode(cf: string): void {
        const previousCf = this.getItalianFiscalCode(this._lead!)

        if (!cf) this._lead!.fiscalIds = this._lead!.fiscalIds.filter(f => f.kind != FiscalIdKind.CfPersonaFisica)
        else if (previousCf) previousCf.value = cf
        else this._lead!.fiscalIds.push({
            id: 0, description: '', value: cf,
            isPreferred: true, kind: FiscalIdKind.CfPersonaFisica
        })
    }

    protected filterConfiguredRawValues(value: LeadRawValue[]): LeadRawValue[] {
        let filteredRawvalues = value

        for (const key in Object.keys(this.config.rawvaluesNamesConfig)) {
            filteredRawvalues = filteredRawvalues
                .filter(rv => !this.config.rawvaluesNamesConfig[Object.keys(this.config.rawvaluesNamesConfig)[key] as keyof RawValueNameConfigurations].includes(rv.description))
        }

        return filteredRawvalues
    }
}